import { type FC, type ReactNode, useMemo, useRef } from 'react';

import { useWindowResize } from '../../../hooks/useWindowResize';
import { type ViewportSeed } from '../../../util/responsive/device-seed';
import { getViewport } from '../../../util/responsive/media-queries';
import { deriveViewportContext, ViewportContextObj } from '../context';
import { useViewportState } from '../internal-hooks/use-viewport-state';
import { type ViewportContext } from '../types';

type Listener = (window: Window) => void;

interface ViewportProviderProps {
  readonly children: ReactNode;
  /**
   * A {@link ViewportSeed} derived on the server from the request User-Agent, used to initialize
   * the viewport state for the initial SSR render and the client's first render so the two agree.
   *
   * Because this provider always attaches a window resize listener, the seed is only the starting
   * value: the listener replaces it with the real window dimensions immediately after mount.  When
   * omitted, the state falls back to {@link InitialWindowSize}.
   */
  readonly initialViewportSeed?: ViewportSeed;
}

export const ViewportProvider: FC<ViewportProviderProps> = ({ children, initialViewportSeed }) => {
  const { setSize, setViewport, size, viewport } = useViewportState({
    initialSize: initialViewportSeed?.size,
    viewport: initialViewportSeed?.viewport,
  });

  const listener = useRef<Listener>((wind: Window) => {
    const view = getViewport({ window: wind });
    setViewport(view);
    setSize(wind.innerWidth);
  });

  useWindowResize(listener.current);

  const context = useMemo<ViewportContext>(
    () => deriveViewportContext({ size, viewport }),
    [viewport, size],
  );

  return <ViewportContextObj value={context}>{children}</ViewportContextObj>;
};
